import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ImageSearch as ImageSearchIcon, CameraAlt as CameraAltIcon, Upload as UploadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toyService, masterDataService } from '../../services/toyService';
import { currencyService, SUPPORTED_CURRENCIES } from '../../services/currencyService';
import { autoImageFinderService } from '../../services/autoImageFinderService';
import { toyFinderService } from '../../services/toyFinderService';
import { useTranslation } from '../../hooks/useTranslation';
import CustomAutocomplete from '../common/CustomAutocomplete';

function ToyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const isNew = id === 'new';

  const [formData, setFormData] = useState({
    brand: '',
    code: '',
    name: '',
    series: '',
    type: '',
    scale: '',
    imageUrl: '',
    qty: 1,
    price: 0,
    preorder_price: 0,
    priceCurrency: 'USD', // Will be updated when user currency is loaded
    preorderPriceCurrency: 'USD', // Will be updated when user currency is loaded
    source: '',
    note: '-'
  });

  const [masterData, setMasterData] = useState({
    brands: [],
    series: [],
    types: [],
    scales: [],
    sources: []
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');
  const [findingImages, setFindingImages] = useState(false);
  const [findingToy, setFindingToy] = useState(false);
  const [imageInputRef, setImageInputRef] = useState(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [videoRef, setVideoRef] = useState(null);

  // Helper function to deduplicate arrays
  const deduplicateArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr)];
  };

  useEffect(() => {
    loadMasterData();
    loadUserCurrency();
    if (!isNew && id && id !== 'new') {
      loadToy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Update default currency when user currency is loaded
  useEffect(() => {
    if (userCurrency && userCurrency !== 'USD') {
      setFormData(prev => ({
        ...prev,
        priceCurrency: userCurrency,
        preorderPriceCurrency: userCurrency
      }));
    }
  }, [userCurrency]);

  // Handle video stream when camera dialog opens
  useEffect(() => {
    if (cameraDialogOpen && cameraStream) {
      // Use a small delay to ensure video element is mounted
      const timer = setTimeout(() => {
        if (videoRef) {
          videoRef.srcObject = cameraStream;
          videoRef.play().catch(error => {
            console.error('Error playing video:', error);
            setError('Failed to start camera preview');
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Cleanup: stop stream when dialog closes
    if (!cameraDialogOpen && videoRef) {
      videoRef.srcObject = null;
    }
  }, [cameraDialogOpen, cameraStream, videoRef]);

  async function loadMasterData() {
    try {
      const data = await masterDataService.getMasterData(currentUser?.uid);
      setMasterData(data);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  }

  async function loadUserCurrency() {
    if (!currentUser?.uid) return;
    
    try {
      const currency = await currencyService.getUserCurrency(currentUser.uid);
      setUserCurrency(currency);
    } catch (error) {
      console.error('Failed to load user currency:', error);
    }
  }

  async function convertPriceToUserCurrency(amount, fromCurrency) {
    if (!amount || fromCurrency === userCurrency) return amount;
    
    try {
      const convertedAmount = await currencyService.convertCurrency(amount, fromCurrency, userCurrency);
      return convertedAmount;
    } catch (error) {
      console.error('Failed to convert currency:', error);
      return amount;
    }
  }

  async function loadToy() {
    try {
      setLoading(true);
      const toy = await toyService.getToyById(id);
      
      if (toy) {
        const formDataToSet = {
          brand: toy.brand || '',
          code: toy.code || '',
          name: toy.name || '',
          series: toy.series || '',
          type: toy.type || '',
          scale: toy.scale || '',
          imageUrl: toy.imageUrl || '',
          qty: toy.qty || 1,
          price: toy.price || 0,
          preorder_price: toy.preorder_price || 0,
          priceCurrency: toy.priceCurrency || 'USD',
          preorderPriceCurrency: toy.preorderPriceCurrency || 'USD',
          source: toy.source || '',
          note: toy.note || '-',
          createdDate: (() => {
            try {
              if (!toy.createdDate) return '';
              if (typeof toy.createdDate === 'string') return toy.createdDate;
              if (toy.createdDate.toDate) {
                const date = toy.createdDate.toDate();
                return date.toISOString().split('T')[0] + 'T00:00Z';
              }
              return '';
            } catch (error) {
              console.error('Error processing createdDate:', error);
              return '';
            }
          })()
        };
        
        setFormData(formDataToSet);
      } else {
        setError('Toy not found');
      }
    } catch (error) {
      console.error('Error loading toy:', error);
      setError('Failed to load toy: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.name) {
      console.error('Validation failed: Missing name');
      setError('Name is a required field');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Convert prices to user's currency before saving
      const price = parseFloat(formData.price) || 0;
      const preorderPrice = parseFloat(formData.preorder_price) || 0;
      
      const convertedPrice = await convertPriceToUserCurrency(price, formData.priceCurrency);
      const convertedPreorderPrice = await convertPriceToUserCurrency(preorderPrice, formData.preorderPriceCurrency);

      // Clean and validate form data before submission
      const cleanFormData = {
        ...formData,
        brand: formData.brand || '',
        code: formData.code || '', // Code is now optional
        name: formData.name || '',
        series: formData.series || '',
        type: formData.type || '',
        scale: formData.scale || '',
        imageUrl: formData.imageUrl || '',
        qty: parseInt(formData.qty) || 1,
        price: convertedPrice,
        preorder_price: convertedPreorderPrice,
        priceCurrency: userCurrency,
        preorderPriceCurrency: userCurrency,
        source: formData.source || '',
        note: formData.note || '-',
        createdDate: (() => {
          try {
            if (!formData.createdDate) return '';
            if (typeof formData.createdDate === 'string') {
              // Ensure it's a valid date string
              if (formData.createdDate.includes('T')) {
                return formData.createdDate;
              }
              return formData.createdDate + 'T00:00Z';
            }
            return '';
          } catch (error) {
            console.error('Error processing createdDate:', error);
            return '';
          }
        })()
      };

      // Simple logic: if id is 'new', create; otherwise, update
      if (isNew) {
        if (!currentUser?.uid) {
          throw new Error('User not authenticated');
        }
        await toyService.createToy(cleanFormData, currentUser.uid);
      } else {
        if (!id) {
          throw new Error('Toy ID is required for update');
        }
        await toyService.updateToy(id, cleanFormData);
      }
      
      navigate('/toys');
    } catch (error) {
      console.error('Error saving toy:', error);
      setError('Failed to save toy: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  function handleInputChange(field, value) {
    try {
      setFormData(prev => ({
        ...prev,
        [field]: value === null || value === undefined ? '' : String(value)
      }));
    } catch (error) {
      console.error(`Error in handleInputChange for field ${field}:`, error);
      setFormData(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }

  async function handleAutoImageFinder() {
    if (!currentUser?.uid) {
      setError('Please log in to use auto image finder');
      return;
    }

    // Require at least name and brand for better results (code is optional)
    if (!formData.name || !formData.brand) {
      setError('Please fill in the toy name and brand for better image search results.');
      return;
    }

    try {
      setFindingImages(true);
      setError('');

      const toyData = {
        name: formData.name.trim(),
        brand: formData.brand.trim(),
        code: formData.code.trim()
      };

      console.log('Searching for images with:', toyData);

      const imageUrl = await autoImageFinderService.findBestImage(toyData, currentUser.uid);
      
      if (imageUrl) {
        setFormData(prev => ({
          ...prev,
          imageUrl: imageUrl
        }));
        // Clear any previous error messages
        setError('');
      } else {
        setError('No images found for this toy. Please try adding more details (series, type) or enter an image URL manually.');
      }
    } catch (error) {
      console.error('Error finding images:', error);
      setError('Failed to find images: ' + error.message + '. Please try again or enter an image URL manually.');
    } finally {
      setFindingImages(false);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    await processImageFile(file);
    
    // Reset the input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  }

  async function handleCameraCapture() {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera is not supported in your browser');
        return;
      }

      // Open camera dialog first
      setCameraDialogOpen(true);

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      setCameraStream(stream);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setError('Failed to access camera: ' + error.message);
      setCameraDialogOpen(false);
    }
  }

  function handleCloseCameraDialog() {
    // Stop the camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (videoRef) {
      videoRef.srcObject = null;
    }
    setCameraDialogOpen(false);
  }

  function handleCapturePhoto() {
    if (!videoRef || !cameraStream) return;

    try {
      // Create a canvas to capture the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = videoRef.videoWidth;
      canvas.height = videoRef.videoHeight;
      ctx.drawImage(videoRef, 0, 0);

      // Stop the stream
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setCameraDialogOpen(false);

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (blob) {
          await processImageFile(blob);
        } else {
          setError('Failed to capture image from camera');
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setError('Failed to capture photo: ' + error.message);
      handleCloseCameraDialog();
    }
  }

  async function processImageFile(imageFile) {
    if (!currentUser?.uid) {
      setError('Please log in to use image finder');
      return;
    }

    try {
      setFindingToy(true);
      setError('');

      console.log('Finding toy from image...');
      const toyData = await toyFinderService.findToyFromImage(imageFile);
      
      if (toyData) {
        // Populate form with the response data
        // Map common field names that might come from the API
        setFormData(prev => ({
          ...prev,
          name: toyData.name || toyData.title || prev.name,
          brand: toyData.brand || prev.brand,
          code: toyData.code || toyData.productCode || prev.code,
          series: toyData.series || prev.series,
          type: toyData.type || toyData.category || prev.type,
          scale: toyData.scale || prev.scale,
          imageUrl: toyData.imageUrl || toyData.image || toyData.img || prev.imageUrl,
          price: toyData.price || prev.price,
          priceCurrency: toyData.priceCurrency || toyData.currency || prev.priceCurrency,
          preorder_price: toyData.preorder_price || toyData.preorderPrice || prev.preorder_price,
          preorderPriceCurrency: toyData.preorderPriceCurrency || toyData.currency || prev.preorderPriceCurrency,
          source: toyData.source || prev.source,
          note: toyData.note || toyData.notes || prev.note
        }));

        setError('');
        console.log('Toy data populated from image:', toyData);
      } else {
        setError('No toy data found from the image. Please fill in the form manually.');
      }
    } catch (error) {
      console.error('Error finding toy from image:', error);
      setError('Failed to find toy from image: ' + error.message);
    } finally {
      setFindingToy(false);
    }
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/toys')}
          sx={{ mb: 2 }}
        >
          {t('common.backToCollection')}
        </Button>
        
        <Typography variant="h4" component="h1" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          {isNew ? t('toyDetail.newToy') : t('toyDetail.editToy')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Image Upload Section */}
        {isNew && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Find Toy from Image
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload an image or take a photo to automatically populate toy information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload-input"
                type="file"
                onChange={handleImageUpload}
                ref={(input) => {
                  if (input && !imageInputRef) {
                    setImageInputRef(input);
                  }
                }}
              />
              <label htmlFor="image-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={findingToy ? <CircularProgress size={20} /> : <UploadIcon />}
                  disabled={findingToy}
                  sx={{ minWidth: 140 }}
                >
                  {findingToy ? 'Finding...' : 'Upload Image'}
                </Button>
              </label>
              <Button
                variant="outlined"
                startIcon={findingToy ? <CircularProgress size={20} /> : <CameraAltIcon />}
                onClick={handleCameraCapture}
                disabled={findingToy}
                sx={{ minWidth: 140 }}
              >
                {findingToy ? 'Capturing...' : 'Use Camera'}
              </Button>
            </Box>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t('toyDetail.name')} *`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('toyDetail.code')}
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                placeholder={t('toyDetail.codePlaceholder') || "Optional product code"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                options={deduplicateArray(masterData.brands)}
                value={formData.brand}
                onChange={(newValue) => handleInputChange('brand', newValue || '')}
                label={t('toyDetail.brand')}
                dataType="brands"
                onOptionsUpdate={(updatedOptions) => {
                  setMasterData(prev => ({
                    ...prev,
                    brands: updatedOptions
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                options={deduplicateArray(masterData.series)}
                value={formData.series}
                onChange={(newValue) => handleInputChange('series', newValue || '')}
                label={t('toyDetail.series')}
                dataType="series"
                onOptionsUpdate={(updatedOptions) => {
                  setMasterData(prev => ({
                    ...prev,
                    series: updatedOptions
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                options={deduplicateArray(masterData.types)}
                value={formData.type}
                onChange={(newValue) => handleInputChange('type', newValue || '')}
                label={t('toyDetail.type')}
                dataType="types"
                onOptionsUpdate={(updatedOptions) => {
                  setMasterData(prev => ({
                    ...prev,
                    types: updatedOptions
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                options={deduplicateArray(masterData.scales)}
                value={formData.scale}
                onChange={(newValue) => handleInputChange('scale', newValue || '')}
                label={t('toyDetail.scale')}
                dataType="scales"
                onOptionsUpdate={(updatedOptions) => {
                  setMasterData(prev => ({
                    ...prev,
                    scales: updatedOptions
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label={t('toyDetail.imageUrl')}
                  value={formData.imageUrl}
                  onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  helperText="Fill in name and brand above for better auto-search results (code is optional)"
                />
                <Button
                  variant="outlined"
                  startIcon={findingImages ? <CircularProgress size={20} /> : <ImageSearchIcon />}
                  onClick={handleAutoImageFinder}
                  disabled={findingImages || !formData.name || !formData.brand}
                  sx={{ minWidth: 'auto', px: 2, py: 1.75 }}
                  title="Search for images automatically using name and brand (code optional)"
                >
                  {findingImages ? 'Searching...' : 'Find Images'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('toyDetail.quantity')}
                type="number"
                value={formData.qty}
                onChange={(e) => handleInputChange('qty', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t('toyDetail.price')}
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>{t('common.currency')}</InputLabel>
                  <Select
                    value={formData.priceCurrency}
                    label={t('common.currency')}
                    onChange={(e) => handleInputChange('priceCurrency', e.target.value)}
                  >
                    {Object.entries(SUPPORTED_CURRENCIES).map(([code, currency]) => (
                      <MenuItem key={code} value={code}>
                        {currency.symbol} {code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t('toyDetail.preorderPrice')}
                  type="number"
                  value={formData.preorder_price}
                  onChange={(e) => handleInputChange('preorder_price', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>{t('common.currency')}</InputLabel>
                  <Select
                    value={formData.preorderPriceCurrency}
                    label={t('common.currency')}
                    onChange={(e) => handleInputChange('preorderPriceCurrency', e.target.value)}
                  >
                    {Object.entries(SUPPORTED_CURRENCIES).map(([code, currency]) => (
                      <MenuItem key={code} value={code}>
                        {currency.symbol} {code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                options={deduplicateArray(masterData.sources)}
                value={formData.source}
                onChange={(newValue) => handleInputChange('source', newValue || '')}
                label={t('toyDetail.source')}
                dataType="sources"
                onOptionsUpdate={(updatedOptions) => {
                  setMasterData(prev => ({
                    ...prev,
                    sources: updatedOptions
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('toyDetail.createdDate')}
                type="date"
                value={(() => {
                  try {
                    if (!formData.createdDate) return '';
                    if (typeof formData.createdDate === 'string') {
                      const datePart = formData.createdDate.split('T')[0];
                      return datePart || '';
                    }
                    return '';
                  } catch (error) {
                    console.error('Error parsing createdDate:', error);
                    return '';
                  }
                })()}
                onChange={(e) => {
                  try {
                    const value = e.target.value;
                    handleInputChange('createdDate', value ? value + 'T00:00Z' : '');
                  } catch (error) {
                    console.error('Error setting createdDate:', error);
                    handleInputChange('createdDate', '');
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('toyDetail.notes')}
                multiline
                rows={3}
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/toys')}
                  disabled={saving}
                >
                  {t('toyDetail.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : (isNew ? t('toyDetail.save') : t('toyDetail.save'))}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Camera Dialog */}
      <Dialog 
        open={cameraDialogOpen} 
        onClose={handleCloseCameraDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Take Photo</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <video
              ref={(ref) => setVideoRef(ref)}
              autoPlay
              playsInline
              style={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                backgroundColor: '#000'
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Position the toy in the frame and click "Capture" to take a photo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCameraDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleCapturePhoto} 
            variant="contained"
            startIcon={<CameraAltIcon />}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ToyDetail; 